import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";
import { invalidatePhotoQueries } from "@/lib/mutationHelpers";

export type ReferencePhotosResult = {
  list: { id: string; property_id: string; room_name: string; photo_url: string; display_order: number | null; created_at: string; checklist_id: string | null }[];
  hasLegacyFallback: boolean;
};

export const useReferencePhotos = (
  propertyId: string | undefined,
  checklistId: string | null | undefined = undefined
) => {
  return useQuery({
    queryKey: queryKeys.photos.reference(propertyId || '', checklistId ?? null),
    queryFn: async (): Promise<ReferencePhotosResult> => {
      if (!propertyId) return { list: [], hasLegacyFallback: false };
      const key = checklistId ?? null;

      if (key != null) {
        const { data: scoped, error: e1 } = await supabase
          .from("reference_photos")
          .select("*")
          .eq("property_id", propertyId)
          .eq("checklist_id", key)
          .order("room_name", { ascending: true })
          .order("display_order", { ascending: true });
        if (e1) throw e1;
        if (scoped && scoped.length > 0) return { list: scoped, hasLegacyFallback: false };

        const { data: legacy, error: e2 } = await supabase
          .from("reference_photos")
          .select("*")
          .eq("property_id", propertyId)
          .is("checklist_id", null)
          .order("room_name", { ascending: true })
          .order("display_order", { ascending: true });
        if (e2) throw e2;
        return { list: legacy ?? [], hasLegacyFallback: true };
      }

      const { data, error } = await supabase
        .from("reference_photos")
        .select("*")
        .eq("property_id", propertyId)
        .is("checklist_id", null)
        .order("room_name", { ascending: true })
        .order("display_order", { ascending: true });
      if (error) throw error;
      return { list: data ?? [], hasLegacyFallback: false };
    },
    enabled: !!propertyId,
  });
};

export const useUploadReferencePhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      roomName,
      file,
      checklistId = null,
    }: {
      propertyId: string;
      roomName: string;
      file: File;
      checklistId?: string | null;
    }) => {
      // Get the current max display_order for this room (scoped to checklist when provided)
      let q = supabase
        .from("reference_photos")
        .select("display_order")
        .eq("property_id", propertyId)
        .eq("room_name", roomName);
      if (checklistId != null) q = q.eq("checklist_id", checklistId);
      else q = q.is("checklist_id", null);
      const { data: existingPhotos } = await q
        .order("display_order", { ascending: false })
        .limit(1);

      const nextOrder = (existingPhotos?.[0]?.display_order ?? -1) + 1;

      // Create a unique filename with timestamp
      const safeRoomName = roomName.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase();
      const ext = file.name.split(".").pop() || "jpg";
      const timestamp = Date.now();
      const filePath = `${propertyId}/${safeRoomName}-${timestamp}.${ext}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from("reference-photos")
        .upload(filePath, file, { upsert: false });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("reference-photos")
        .getPublicUrl(filePath);

      const photoUrl = urlData.publicUrl;

      const insertRow: { property_id: string; room_name: string; photo_url: string; display_order: number; checklist_id?: string | null } = {
        property_id: propertyId,
        room_name: roomName,
        photo_url: photoUrl,
        display_order: nextOrder,
      };
      if (checklistId != null) insertRow.checklist_id = checklistId;

      const { error: dbError } = await supabase
        .from("reference_photos")
        .insert(insertRow);

      if (dbError) throw dbError;

      return { photoUrl, propertyId, checklistId: checklistId ?? null };
    },
    onSuccess: (data) => {
      invalidatePhotoQueries(queryClient, data.propertyId, data.checklistId);
    },
  });
};

export const useDeleteReferencePhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      propertyId,
      photoUrl,
      checklistId = null,
    }: {
      id: string;
      propertyId: string;
      photoUrl: string;
      checklistId?: string | null;
    }) => {
      // Delete from database
      const { error: dbError } = await supabase
        .from("reference_photos")
        .delete()
        .eq("id", id);

      if (dbError) throw dbError;

      // Try to delete from storage (extract path from URL)
      try {
        const urlParts = photoUrl.split("/reference-photos/");
        if (urlParts[1]) {
          await supabase.storage.from("reference-photos").remove([urlParts[1]]);
        }
      } catch (e) {
        console.warn("Could not delete storage file:", e);
      }

      return { propertyId, checklistId: checklistId ?? null };
    },
    onSuccess: (data) => {
      invalidatePhotoQueries(queryClient, data.propertyId, data.checklistId);
    },
  });
};
